// reportSettings.mjs — 管理页「资方进度报告设置」对话框的纯逻辑（SB-202 S3，第三百零八批；需求方指令
// 「在合适的位置增加用来调整项目层设置的页面：是否汇报、汇报间隔、汇报目标成员、从汇报里排除的版本等」）。
//
// 四件可判定的事集中在这里、配单测：
//   · 报文 → 表单文本（收件人一行一个、版本逗号分隔、闸门一行 `日期 | 中文 | English`）；
//   · 表单文本 → 请求体：邮件形、间隔 1～60 天、闸门日期形——服务端会再拒一次（400），但 400 之前页面就该说清是哪一格；
//   · **只送改了的字段**：端点是「只改给了的字段」，把没动的也送回去会让审计明细写成"改了五项"——审计就该记真改了的；
//   · 表格那一格的摘要（开／关 · 每 N 天 · 收件 M 人）。
// 语言随界面：只出 i18n 键，不落文案。

export const EVERY_MIN = 1;
export const EVERY_MAX = 60;

export function splitList(text) {
  return String(text ?? '').split(/[\s,;，；]+/).map((s) => s.trim()).filter(Boolean);
}
export function uniq(list) {
  return [...new Set(list)];
}
export function isEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s ?? ''));
}
export function isDate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s ?? ''));
  if (!m) return false;
  const mo = Number(m[2]), d = Number(m[3]);
  return mo >= 1 && mo <= 12 && d >= 1 && d <= 31;
}

// 报文（admin GET 投影的 `report` 字段）→ 表单文本。缺席字段按缺省（与服务端 ReportSettings 缺省同）。
export function formOf(report) {
  const r = report && typeof report === 'object' ? report : {};
  return {
    enabled: Boolean(r.enabled),
    everyDays: String(Number.isInteger(r.everyDays) ? r.everyDays : 3),
    excludeVersions: (Array.isArray(r.excludeVersions) ? r.excludeVersions : []).join(', '),
    recipients: (Array.isArray(r.recipients) ? r.recipients : []).join('\n'),
    gates: (Array.isArray(r.gates) ? r.gates : []).map((g) => `${g.date} | ${g.zh || ''} | ${g.en || ''}`).join('\n'),
  };
}

// 闸门文本：一行一枚，`YYYY-MM-DD | 中文 | English`（英文缺省抄中文；只写日期也收）。空行跳过。
export function parseGatesText(text) {
  const gates = [];
  for (const raw of String(text ?? '').split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const [date, zh = '', en = ''] = line.split('|').map((s) => s.trim());
    if (!isDate(date)) return { ok: false, bad: line, gates: [] };
    gates.push({ date, zh, en: en || zh });
  }
  return { ok: true, gates };
}

// 表单 → 请求体。problems 按格给键；ok 时 body 是**完整**设置（diffOf 再挑出改了的）。
export function parseForm(form) {
  const f = form || {};
  const problems = {};
  const everyDays = Number(String(f.everyDays ?? '').trim());
  if (!Number.isInteger(everyDays) || everyDays < EVERY_MIN || everyDays > EVERY_MAX) problems.everyDays = 'admin.groups.report.err.every';
  const recipients = uniq(splitList(f.recipients));
  const badMail = recipients.find((r) => !isEmail(r));
  if (badMail) problems.recipients = 'admin.groups.report.err.email';
  const excludeVersions = uniq(splitList(f.excludeVersions));
  if (excludeVersions.some((v) => v.length > 40)) problems.excludeVersions = 'admin.groups.report.err.version';
  const g = parseGatesText(f.gates);
  if (!g.ok) problems.gates = 'admin.groups.report.err.gate';
  if (Object.keys(problems).length) return { ok: false, problems };
  return { ok: true, problems, body: { enabled: Boolean(f.enabled), everyDays, excludeVersions, recipients, gates: g.gates } };
}

// 只送改了的字段：与现值逐项比（数组按序比、闸门三元逐项比）。没有一项变 ⇒ {}。
export function diffOf(before, body) {
  const b = before && typeof before === 'object' ? before : {};
  const same = (x, y) => JSON.stringify(x ?? null) === JSON.stringify(y ?? null);
  const out = {};
  if (Boolean(b.enabled) !== body.enabled) out.enabled = body.enabled;
  if ((Number.isInteger(b.everyDays) ? b.everyDays : 3) !== body.everyDays) out.everyDays = body.everyDays;
  if (!same(Array.isArray(b.excludeVersions) ? b.excludeVersions : [], body.excludeVersions)) out.excludeVersions = body.excludeVersions;
  if (!same(Array.isArray(b.recipients) ? b.recipients : [], body.recipients)) out.recipients = body.recipients;
  const gatesBefore = (Array.isArray(b.gates) ? b.gates : []).map((x) => ({ date: x.date, zh: x.zh || '', en: x.en || '' }));
  if (!same(gatesBefore, body.gates)) out.gates = body.gates;
  return out;
}

// 表格那一格：{enabled, everyDays, recipients: n, exclude: n, gates: n}
export function summaryOf(report) {
  const r = report && typeof report === 'object' ? report : {};
  return {
    enabled: Boolean(r.enabled),
    everyDays: Number.isInteger(r.everyDays) ? r.everyDays : 3,
    recipients: Array.isArray(r.recipients) ? r.recipients.length : 0,
    exclude: Array.isArray(r.excludeVersions) ? r.excludeVersions.length : 0,
    gates: Array.isArray(r.gates) ? r.gates.length : 0,
  };
}
